import * as actionTypes from "./actionTypes";
import {
  getIssuePermissionsApi,
  deleteIssuePermissionAPI,
  createIssuePermissionApi,
  updateIssuePermissionApi,
  importIssuePermissionsExcelApi,
  downloadIssuePermissionsExcelApi,
  getIssuePermissionsBalanceApi,
  generateFileFromIssuePermissionApi,
  createIssuePermissionsBalanceApi,
  deleteIssuePermissionsBalanceApi,
} from "@icarius-connection/api";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString, getErrorStringFromServerError } from "@icarius-localization/strings";
import { getFileExtension, getFileName } from "@icarius-utils/fileUpload";
import { downloadBlob } from "@icarius-utils/download";

const errorHandler = (e, dispatch) => {
  let errorString = "";
  switch (e.response?.data?.status) {
    case "INVALID_DATA":
    case "VALIDATION_ERROR":
      errorString = getLocalizedString("validationError");
      break;
    case "IN_USE":
      errorString = getLocalizedString("itIsNotPossibleToDeleteThePermissionBecauseItIsInUse");
      break;
    case "ERROR_MAX_TIME_PER_DAY":
      errorString = getLocalizedString("permissionExceedsAvailableDailyTimeForEmployee");
      break;
    case "DUPLICATED_DATA":
      errorString = getLocalizedString("incidentsAlreadyExistInDateRangePermissionCannotBeSaved");
      break;
    case "ERROR_DAYS_QUANTITY":
      errorString = getLocalizedString("errorDaysQuantityRequests");
      break;
    case "ERROR_FORMAT_DATES":
      errorString = getLocalizedString("formatDateErrorRequests");
      break;
    case "ERROR_DATES":
      errorString = getLocalizedString("dateErrorRequests");
      break;
    case "ERROR_OVERLAP":
      errorString = getLocalizedString("overlapErrorRequests");
      break;
    case "ERROR_EXISTS":
      errorString = getLocalizedString("youHaveASubmissionWithPendingDocumentationApproval");
      break;
    case "ERROR_MAX_DAYS":
      errorString = getLocalizedString("enteredQuantityExceedsMaximumDaysForPermissionType");
      break;
    case "ERROR_NO_DATE_AVAILABLE":
      errorString = e?.response?.data?.message;
      break;
    default:
      errorString = getErrorStringFromServerError(e);
      break;
  }
  dispatch(openSnackbarAction({ msg: errorString, severity: "error" }));
}

export const getIssuePermissionsAction = () => async (dispatch) => {
  dispatch({ type: actionTypes.GET });
  try {
    let response = await getIssuePermissionsApi();

    let data = response.data && response.data.data;
    let selects = response.data && response.data.selects;
    let status = response.data && response.data.status;
    let hasSignedDocument = response.data && response.data.hasSignedDocument;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_FULFILLED,
      payload: {
        data,
        selects,
        hasSignedDocument,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const deleteIssuePermissionAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE });
  try {
    let response = await deleteIssuePermissionAPI(dataToSend);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("permissionDeletedOK"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const createIssuePermissionAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE });
  try {
    let response = await createIssuePermissionApi(dataToSend);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("permissionCreatedOK"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const updateIssuePermissionAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.MODIFY });
  try {
    let response = await updateIssuePermissionApi(dataToSend);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.MODIFY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.MODIFY_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("permissionUpdatedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.MODIFY_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const downloadStructureAction = () => async (dispatch) => {
  dispatch({ type: actionTypes.DOWNLOAD });
  try {
    let response = await downloadIssuePermissionsExcelApi({ type: "PERMISSIONS", empty: false });
    let title = response.headers["x-content-namefile"];

    if (response.status !== 200) {
      dispatch({ type: actionTypes.DOWNLOAD_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DOWNLOAD_FULFILLED,
    });

    downloadBlob(response.data, getFileExtension(title), getFileName(title));

    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DOWNLOAD_REJECTED });
    dispatch(openSnackbarAction({ msg: getErrorStringFromServerError(e), severity: "error" }));
  }
};

export const importSpreadsheetAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPLOAD });
  try {
    let formData = new FormData();
    formData.append('file', dataToSend.file);

    let response = await importIssuePermissionsExcelApi(formData);
    let title = response.headers["x-content-namefile"];
    let hasErrors = title && title.includes("Errores-");
    if (title) {
      title = title.replace("Errores-", "");
    }
    if (response.status !== 200) {
      dispatch({ type: actionTypes.UPLOAD_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPLOAD_FULFILLED,
    });

    if (title) {
      downloadBlob(response.data, getFileExtension(title), getFileName(title));
      dispatch(openSnackbarAction({
        msg: getLocalizedString("importFinishedCheckFileForResult"),
        severity: !hasErrors ? "success" : "warning",
        duration: null
      }));
      return response;
    }
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPLOAD_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const getIssuePermissionsBalanceAction = (code) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_BALANCE });
  try {
    let response = await getIssuePermissionsBalanceApi(`?code=${code}`);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_BALANCE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_BALANCE_FULFILLED,
      payload: { data },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_BALANCE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const generateFileFromIssuePermissionAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.GENERATE });
  try {
    let response = await generateFileFromIssuePermissionApi(dataToSend);
    let reception = response.data && response.data.reception;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GENERATE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GENERATE_FULFILLED,
    });

    dispatch(openSnackbarAction({
      msg:
        reception
          ? getLocalizedString("documentGeneratedSuccessfullyAndSentForEmployeeReceipt")
          : getLocalizedString("documentGeneratedSuccessfullyAndSentToEmployeeDigitalFolder"),
      severity: "success"
    }));

    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GENERATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const createIssuePermissionsBalanceAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE_BALANCE });
  try {
    let response = await createIssuePermissionsBalanceApi(dataToSend);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_BALANCE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_BALANCE_FULFILLED,
      payload: { data },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_BALANCE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const deleteIssuePermissionsBalanceAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_BALANCE });
  try {
    let response = await deleteIssuePermissionsBalanceApi(dataToSend);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_BALANCE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_BALANCE_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("recordDeletedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_BALANCE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};